import React, { useEffect, useState } from 'react';
import { Star, CheckCircle, Clock, Trash2, Edit2, Car, RotateCcw } from 'lucide-react';
import { useAuth } from '../../Context/AuthContext';
import Swal from 'sweetalert2';
import { API_URL } from '../../config';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (err) {
      console.error('Yorumlar yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [token]);

  const handleStatusChange = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/admin/reviews/${id}/status`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_approved: status })
      });
      if (response.ok) {
        Swal.fire({ 
          icon: 'success', 
          title: status === 1 ? 'Onaylandı!' : 'Geri Alındı!', 
          background: '#0f172a', 
          color: '#fff', 
          timer: 1500 
        });
        fetchReviews();
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Hata!', background: '#0f172a', color: '#fff' });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Emin misiniz?',
      text: "Bu yorumu kalıcı olarak silmek istiyor musunuz?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#334155',
      confirmButtonText: 'Evet, Sil!',
      background: '#0f172a',
      color: '#fff'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/admin/reviews/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          Swal.fire({ icon: 'success', title: 'Silindi!', background: '#0f172a', color: '#fff', timer: 1500 });
          fetchReviews();
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Hata!', background: '#0f172a', color: '#fff' });
      }
    }
  };

  const renderStars = (rating) => {
    return (
      <div style={{ display: 'flex', gap: '2px' }}>
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            size={14} 
            fill={i < rating ? "#00d1ff" : "none"} 
            color={i < rating ? "#00d1ff" : "var(--text-dim)"} 
          />
        ))}
      </div>
    );
  };

  if (loading) return <div className="admin-card">Yükleniyor...</div>;

  return (
    <div className="admin-reviews">
      <div style={{ marginBottom: '2rem' }}>
        <h1 className="admin-card-title" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', marginBottom: '0.25rem' }}>Yorum Yönetimi</h1>
        <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>Müşteri değerlendirmelerini inceleyin ve paneli yönetin.</p>
      </div>

      <div className="admin-card" style={{ borderRadius: '1.25rem', border: '1px solid var(--navbar-border)', padding: 0, overflow: 'hidden' }}>
        <div className="admin-table-wrapper">
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead style={{ background: 'rgba(255,255,255,0.02)' }}>
              <tr>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Müşteri</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>İletişim</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>İlgili Araç</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Puan</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Yorum</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Durum</th>
                <th style={{ padding: '1rem 1.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {reviews.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-dim)' }}>Henüz yorum bulunmuyor.</td>
                </tr>
              ) : (
                reviews.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid var(--navbar-border)' }}>
                    <td style={{ padding: '1rem 1.5rem', fontWeight: '700', whiteSpace: 'nowrap' }}>{item.name} {item.surname}</td>
                    <td style={{ padding: '1rem 1.5rem', color: 'var(--text-dim)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      {item.phone ? (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>📞 {item.phone}</span>
                          {item.email && <span style={{ opacity: 0.6, fontSize: '0.75rem' }}>{item.email}</span>}
                        </div>
                      ) : (
                        item.email || '-'
                      )}
                    </td>
                    <td style={{ padding: '1rem 1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
                        <Car size={14} /> {item.vehicle_name || 'Genel Yorum'}
                      </div>
                    </td>
                    <td style={{ padding: '1rem 1.5rem' }}>{renderStars(item.stars)}</td>
                    <td style={{ padding: '1rem 1.5rem' }}>
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-main)', lineHeight: '1.5', minWidth: '250px', maxWidth: '400px' }}>
                        "{item.review_text}"
                      </p>
                    </td>
                    <td style={{ padding: '1rem 1.5rem' }}>
                      <span className={`badge ${item.is_approved === 1 ? 'badge-success' : 'badge-warning'}`} style={{ whiteSpace: 'nowrap' }}>
                        {item.is_approved === 1 ? <CheckCircle size={12} style={{ marginRight: '4px' }} /> : <Clock size={12} style={{ marginRight: '4px' }} />}
                        {item.is_approved === 1 ? 'Onaylandı' : 'Beklemede'}
                      </span>
                    </td>
                    <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', whiteSpace: 'nowrap' }}>
                        {item.is_approved === 0 ? (
                          <button 
                            onClick={() => handleStatusChange(item.id, 1)} 
                            style={{ background: 'rgba(34, 197, 94, 0.05)', border: 'none', color: '#4ade80', padding: '0.4rem', borderRadius: '0.5rem', cursor: 'pointer' }} 
                            title="Onayla"
                          >
                            <CheckCircle size={16} />
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleStatusChange(item.id, 0)} 
                            style={{ background: 'rgba(255, 255, 255, 0.05)', border: 'none', color: 'var(--text-dim)', padding: '0.4rem', borderRadius: '0.5rem', cursor: 'pointer' }} 
                            title="Onayı Kaldır (Geri Al)"
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                        <button onClick={() => handleDelete(item.id)} style={{ background: 'rgba(239, 68, 68, 0.05)', border: 'none', color: '#f87171', padding: '0.4rem', borderRadius: '0.5rem', cursor: 'pointer' }} title="Sil">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Reviews;

